import { useEffect, useState } from "react";
import { Trash01 } from "@untitledui/icons";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { Modal } from "@/components/application/modals/modal-controlled";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";

/**
 * UC-COMP-001 GdprErasureConfirmModal — 2-step destructive confirm (Phase 32,
 * REQ-COMP-001 + REQ-UI-016).
 *
 * Workflow:
 *   1. **Subject summary review** — admin vidí target email + display_name +
 *      role; tlačítko "Pokračovat na potvrzení" (cancel always-on).
 *   2. **Typed confirmation + countdown** — text input requires exact match
 *      `"erase <userId>"` + 5-sekundový countdown disable Submit button.
 *      Po expiraci countdownu se "Provést výmaz (nevratné)" rozsvítí.
 *
 * Po úspěšném erase parent přepne `open` na false a renderne completion screen
 * mimo Modal (audit pointer link). UI komponenta jen handle 2-step flow.
 *
 * Accessibility:
 *   - role="dialog" + aria-modal (via Modal primitive)
 *   - aria-live status oznámení pro countdown progression
 *   - keyboard navigation (Tab/Esc) via Modal focus-trap
 */

export const GDPR_ERASURE_PHRASE_PREFIX = "erase";
export const GDPR_ERASURE_COUNTDOWN_SECONDS = 5;

export interface GdprErasureConfirmModalProps {
  open: boolean;
  /** Cíl výmazu — UUID. */
  userId: string;
  /** Email cílového uživatele (pre-erasure snapshot, pro audit summary). */
  email: string;
  /** Display name (pre-erasure). */
  displayName: string;
  /** RBAC role. */
  role: string;
  submitting?: boolean;
  submitError?: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}

type Step = 1 | 2;

export function GdprErasureConfirmModal(props: GdprErasureConfirmModalProps) {
  const {
    open,
    userId,
    email,
    displayName,
    role,
    submitting,
    submitError,
    onCancel,
    onConfirm,
  } = props;

  const [step, setStep] = useState<Step>(1);
  const [phrase, setPhrase] = useState<string>("");
  const [remaining, setRemaining] = useState<number>(GDPR_ERASURE_COUNTDOWN_SECONDS);

  // Reset state on open.
  useEffect(() => {
    if (open) {
      setStep(1);
      setPhrase("");
      setRemaining(GDPR_ERASURE_COUNTDOWN_SECONDS);
    }
  }, [open]);

  // Countdown timer — jen on step 2.
  useEffect(() => {
    if (!open || step !== 2 || remaining <= 0) return;
    const t = setTimeout(() => setRemaining((r) => Math.max(0, r - 1)), 1000);
    return () => clearTimeout(t);
  }, [open, step, remaining]);

  const expectedPhrase = `${GDPR_ERASURE_PHRASE_PREFIX} ${userId}`;
  const phraseValid = phrase.trim().toLowerCase() === expectedPhrase.toLowerCase();
  const countdownExpired = remaining <= 0;
  const submitDisabled = !phraseValid || !countdownExpired || submitting === true;

  return (
    <Modal
      open={open}
      title="Výmaz osobních údajů uživatele (GDPR)"
      icon={Trash01}
      iconColor="error"
      onClose={onCancel}
      footer={renderFooter()}
    >
      {step === 1 && (
        <div data-testid="gdpr-erasure-step-1">
          <p>
            <strong>Připravujete nevratný výmaz osobních údajů uživatele.</strong>{" "}
            Po potvrzení se nevratně odstraní všechny osobní údaje uživatele — účet,
            auditní záznamy, transakce, e-maily, souhlasy a doručení push-assist. Jde
            o výkon práva na výmaz podle GDPR.
          </p>
          <dl style={{ marginTop: "1rem" }}>
            <dt>Email</dt>
            <dd>{email}</dd>
            <dt>Jméno</dt>
            <dd>{displayName}</dd>
            <dt>Role</dt>
            <dd>{role}</dd>
            <dt>User ID</dt>
            <dd>
              <code>{userId}</code>
            </dd>
          </dl>
          <InlineAlert variant="warning" title="Tato akce je nevratná">
            Po výmazu nelze osobní data obnovit. Pokračujte pouze na základě platného
            subject access request (viz runbook).
          </InlineAlert>
        </div>
      )}

      {step === 2 && (
        <div data-testid="gdpr-erasure-step-2">
          <p>
            Pro potvrzení napište přesně:{" "}
            <code data-testid="gdpr-erasure-expected-phrase">{expectedPhrase}</code>
          </p>
          <Input
            label="Potvrzovací fráze"
            data-testid="gdpr-erasure-phrase-input"
            value={phrase}
            onChange={setPhrase}
            autoComplete="off"
            autoFocus
          />
          <p aria-live="polite" data-testid="gdpr-erasure-countdown">
            {countdownExpired
              ? "Můžete provést výmaz."
              : `Čekejte ${remaining} s před odesláním…`}
          </p>
          {submitError && (
            <InlineAlert variant="error" title="Chyba">
              {submitError}
            </InlineAlert>
          )}
        </div>
      )}
    </Modal>
  );

  function renderFooter() {
    if (step === 1) {
      return (
        <>
          <Button color="secondary" onPress={onCancel} data-testid="gdpr-erasure-cancel-button">
            Zrušit
          </Button>
          <Button
            color="primary"
            onPress={() => setStep(2)}
            data-testid="gdpr-erasure-next-step-button"
          >
            Pokračovat na potvrzení
          </Button>
        </>
      );
    }
    return (
      <>
        <Button color="secondary" onPress={() => setStep(1)} data-testid="gdpr-erasure-back-button">
          Zpět
        </Button>
        <Button
          color="primary-destructive"
          onPress={onConfirm}
          isDisabled={submitDisabled}
          data-testid="gdpr-erasure-submit-button"
        >
          {submitting ? "Provádím výmaz…" : "Provést výmaz (nevratné)"}
        </Button>
      </>
    );
  }
}
